import curves from "./data/curves.json";
import roster from "./data/roster.json";
import { demoSpanSeconds } from "./clock";

const STEP = curves.stepSeconds;
const N = curves.n;
type Field = "current_l1"|"current_l2"|"current_l3"|"active_power"|"apparent_power"|"power_factor"|"voltage_l1"|"frequency";

function sampleField(sid: string, field: Field, second: number): number {
  const arr = (curves.services as Record<string, Record<Field, (number|null)[]>>)[sid]?.[field];
  if (!arr) return 0;
  const x = Math.min(Math.max(second / STEP, 0), N - 1);
  const i0 = Math.floor(x), i1 = Math.min(i0 + 1, N - 1), frac = x - i0;
  const a = arr[i0], b = arr[i1];
  if (a == null && b == null) return 0;
  if (a == null) return b as number;
  if (b == null) return a as number;
  return a + (b - a) * frac;
}

export function interpolateAt(second: number, sid: string) {
  return {
    i: [sampleField(sid,"current_l1",second), sampleField(sid,"current_l2",second), sampleField(sid,"current_l3",second)] as [number,number,number],
    p: sampleField(sid,"active_power",second),
    s: sampleField(sid,"apparent_power",second),
    pf: sampleField(sid,"power_factor",second) || 0.95,
    v: sampleField(sid,"voltage_l1",second) || 122,
    f: sampleField(sid,"frequency",second) || 60,
  };
}

// Tiny deterministic jitter so tiles flutter without distorting the real curve.
function jitter(seed: number, amp: number): number {
  const x = Math.sin(seed * 12.9898) * 43758.5453;
  return (x - Math.floor(x) - 0.5) * 2 * amp;
}

const SIDS = roster.services.map((s: { system_id: string }) => s.system_id);
const SUBPOINTS = 5;

export function buildDynoSamples(nowMs: number) {
  const base = demoSpanSeconds(nowMs);
  const systems = SIDS.map((sid) => {
    const ts: number[] = [], cl1:number[]=[], cl2:number[]=[], cl3:number[]=[], cn:number[]=[],
      apT:number[]=[], sT:number[]=[], pfT:number[]=[], fT:number[]=[];
    for (let k = 0; k < SUBPOINTS; k++) {
      const sec = base + k * 2;                 // ~2s sub-spacing
      const v = interpolateAt(sec, sid);
      const j = jitter(nowMs + k, 0.02);
      ts.push(nowMs + k * 200);
      cl1.push(Math.max(0, v.i[0] * (1 + j))); cl2.push(Math.max(0, v.i[1] * (1 + j))); cl3.push(Math.max(0, v.i[2] * (1 + j)));
      cn.push(Math.abs(v.i[0]-v.i[1])*0.1);
      apT.push(v.p * (1 + j)); sT.push(v.s * (1 + j)); pfT.push(v.pf); fT.push(v.f);
    }
    return { system_id: sid, ts, current_l1: cl1, current_l2: cl2, current_l3: cl3, current_n: cn,
             active_power: apT, apparent_power: sT, power_factor: pfT, frequency: fT };
  });
  return { type: "dyno_samples" as const, node_id: "demo-node-a", ts: nowMs, systems };
}

interface SnapshotService {
  v: [number, number, number];
  i: [number, number, number];
  p: number; s: number; pf: number; f: number; i_n: number;
  demand_w: number; demand_source: string; meter_model: string; sig: string;
  // synthesized / illustrative (labeled in README; not recorded AG data):
  inominal_amps: number; inominal_status: string; flagged: boolean;
  dominant_severity: string; events_last_hour: number;
}

export function buildDynoSnapshot(nowMs: number) {
  const sec = demoSpanSeconds(nowMs);
  const services: Record<string, SnapshotService> = {};
  for (const sid of SIDS) {
    const v = interpolateAt(sec, sid);
    const peak = Math.max(v.i[0], v.i[1], v.i[2]);
    services[sid] = {
      v: [v.v, v.v, v.v], i: v.i, p: v.p, s: v.s, pf: v.pf, f: v.f, i_n: 0,
      demand_w: v.p * 1000, demand_source: "derived", meter_model: "acuvim3",
      sig: "ok",
      // synthesized / illustrative (labeled in README; not recorded AG data):
      inominal_amps: 200, inominal_status: "ok", flagged: peak > 300,
      dominant_severity: peak > 400 ? "moderate" : "none", events_last_hour: peak > 300 ? 3 : 0,
    };
  }
  return { type: "dyno_snapshot" as const, ts: nowMs, services };
}
